<template>
    <div
        class="c-view c-view-sticky-footer c-entities"
        v-bind:class="{ fullscreen: isFullscreen }"
    >
        <InternalHeader>
            <div slot="title" class="c-internal-header__title">
                All Contacts
            </div>
        </InternalHeader>

        <div class="c-view-sticky-footer__content">
            <Row class="mb-4 util-min-h40">
                <Col span="24">
                    <div v-if="filtersReady">
                        <entity-filters-edit
                            :filter-group="filterGroup"
                            :available-filters="availableFilters"
                            @updated="onEntityFiltersUpdated"
                        >
                            <ownership-toggle
                                slot="owner"
                                v-bind:value.sync="owner"
                                class="mr-2"
                            ></ownership-toggle>
                            <segment-save
                                slot="save-segment"
                                @save="saveSegment"
                            >
                                <Button
                                    slot="trigger"
                                    custom-icon="icon icon-floppy-disk"
                                    class="text-primary"
                                    >Save as Segment</Button
                                >
                            </segment-save>
                        </entity-filters-edit>
                    </div>
                </Col>
            </Row>

            <Row class="mb-4">
                <Col span="24">
                    <div v-if="statisticsReady">
                        <segment-statistics
                            :pinnable="false"
                            :read-only="false"
                            :modifiers="modifiers"
                            :selected-statistics="selectedStatistics"
                            :available-statistics="availableStatistics"
                            :filter-group="filterGroup"
                            :entity-search-repository="contactSearchRepository"
                        ></segment-statistics>
                    </div>
                </Col>
            </Row>

            <Row class="c-entities__table-container">
                <Col span="24">
                    <Row type="flex" justify="space-between" class="mb-4">
                        <div>
                            <div v-if="columnsReady">
                                <column-select
                                    :available-columns="availableColumns"
                                    :selected-columns="selectedColumns"
                                >
                                    <Button
                                        slot="trigger"
                                        href="javascript:void(0)"
                                        class="text-primary"
                                    >
                                        {{
                                            $t(
                                                "entityColumns.labels.buttonText",
                                                {
                                                    count: selectedColumns.length,
                                                }
                                            )
                                        }}
                                    </Button>
                                </column-select>
                            </div>
                        </div>
                        <div>
                            <Tooltip content="Full screen" :delay="1000">
                                <Button
                                    class="text-secondary"
                                    custom-icon="icon icon-expand-3"
                                    :custom-icon="
                                        isFullscreen
                                            ? 'icon icon-shrink-2'
                                            : 'icon icon-expand-3'
                                    "
                                    @click.stop.prevent="
                                        isFullscreen = !isFullscreen
                                    "
                                />
                            </Tooltip>
                        </div>
                    </Row>

                    <Row class="mb-4">
                        <Col span="24">
                            <contacts-table
                                @sort="onSort"
                                :loading="loading"
                                :rows="dataRows"
                                :columns="dataColumns"
                                :sort-by="sortBy"
                                :border="true"
                                :filter-enabled="true"
                                v-bind:name-filter.sync="nameFilter"
                                v-bind:email-filter.sync="emailFilter"
                                :can-star="false"
                            ></contacts-table>
                        </Col>
                    </Row>
                </Col>
            </Row>
        </div>
        <Footer class="c-view-sticky-footer__footer">
            <Pagination
                :current="pageCurrent"
                :total="pageTotal"
                :page-size="pageSize"
                @page-change="onPageChange"
                @page-size-change="onPageSizeChange"
                show-sizer
                :page-size-opts="[10, 25, 50, 100]"
            />
        </Footer>
    </div>
</template>

<script>
import FilterGroup from "@/js/components/partials/filters/models/filter-group";
import AvailableAccountColumns from "@/js/lookups/account-columns";
import AvailableContactColumns from "@/js/lookups/contact-columns";
import AvailableAccountFilters from "@/js/lookups/account-filters.js";
import AvailableContactFilters from "@/js/lookups/contact-filters.js";
import AvailableStatistics from "@/js/lookups/contact-statistics.js";
import Statistics from "@/js/mixins/statistics.js";

export default {
    mixins: [Statistics],
    props: {
        contactSearchRepository: {
            type: Object,
            default: () => {
                return new (require("@/js/repositories/contact-search.js"))();
            },
        },
        segmentsRepository: {
            type: Object,
            default: () => {
                return new (require("@/js/repositories/segments.js"))();
            },
        },
    },
    data: function () {
        return {
            saving: false,
            saveAsData: {},
            loading: false,
            isFullscreen: false,
            availableFilters: null,
            availableColumns: null,
            availableStatistics: null,
            localStorageKeyFilters: "contacts_filters",
            localStorageKeySortBy: "contacts_sortBy",
            localStorageKeyOwner: "contacts_owner",
            localStorageKeyPageSize: "contacts_pageSize",
            localStorageKeyColumnsSelected: "columns_selected_contacts",
            localStorageKeyStatistics: "statistics_selected_contacts",
            filterGroup: null,
            nameFilter: null, // STRING / QUICK FILTER
            emailFilter: null,
            sortBy: null,
            owner: "all",
            data: null,
            pageCurrent: 1,
            pageSize: null,
            selectedColumns: [],
            selectedStatistics: null,
        };
    },
    components: {
        ContactsTable: function () {
            return import("@/js/components/partials/contacts-table");
        },
        ColumnSelect: function () {
            return import("@/js/components/partials/column-select");
        },
        EntityFiltersEdit: function () {
            return import("@/js/components/partials/filters/edit.vue");
        },
        InternalHeader: function () {
            return import("@/js/components/partials/header");
        },
        OwnershipToggle: function () {
            return import("@/js/components/partials/accounts-ownership-toggle");
        },
        Pagination: function () {
            return import("@/js/components/partials/pagination");
        },
        SegmentSave: function () {
            return import("@/js/components/partials/segment-save");
        },
        SegmentStatistics: function () {
            return import("@/js/components/partials/segment-statistics");
        },
    },
    created() {
        this.query = _.debounce(this.query, 1000, {
            leading: false,
            trailing: true,
        });
        this.load();
    },
    computed: {
        columnsReady() {
            return (
                this.selectedColumns &&
                true === _.get(this.availableColumns, "initialized", false)
            );
        },
        dataColumns() {
            let columnData = _.get(this.data, "columns", []);
            return _.map(columnData, function (column) {
                return Object.assign(column, {
                    title: column.label,
                });
            });
        },
        dataRows() {
            return _.get(this.data, "data", []);
        },
        filtersReady() {
            return (
                this.filterGroup &&
                true === _.get(this.availableFilters, "initialized", false)
            );
        },
        modifiers() {
            return [this.owner];
        },
        pageTotal() {
            return _.get(this.data, "meta.total", 0);
        },
        sortByFormatted() {
            // There should be a default set but if not, return null
            if (!_.get(this, "sortBy.column")) {
                return null;
            }

            let parts = [
                this.sortBy.column,
                this.sortBy.order || "asc",
                this.sortBy.dataTypeId,
            ];

            return parts.filter(Boolean).join(":");
        },
        statisticsReady() {
            return (
                this.selectedStatistics &&
                true === _.get(this.availableStatistics, "initialized", false)
            );
        },
    },
    watch: {
        nameFilter() {
            this.query();
        },
        emailFilter() {
            this.query();
        },
    },
    methods: {
        initializeFilters() {
            var filterGroup = this.$localStorageCache.get(
                this.localStorageKeyFilters
            );
            this.filterGroup = new FilterGroup(
                JSON.parse(filterGroup || "{}"),
                this.availableFilters
            );
            this.$watch("filterGroup", this.onEntityFiltersUpdated, {
                deep: true,
            });
        },
        initializeColumns() {
            var selectedColumns = this.$localStorageCache.get(
                this.localStorageKeyColumnsSelected
            );
            this.selectedColumns = selectedColumns
                ? JSON.parse(selectedColumns)
                : [];
            this.$watch("selectedColumns", this.onColumnsChange, {
                deep: true,
            });
        },
        initializeSortBy() {
            var sortBy = this.$localStorageCache.get(
                this.localStorageKeySortBy
            );
            this.sortBy = JSON.parse(sortBy) || {
                column: "pers__full_name",
                order: "asc",
                dataTypeId: "1",
            };
            this.$watch("sortBy", this.onSortByChange, { deep: true });
        },
        initializeOwner() {
            var owner = this.$localStorageCache.get(this.localStorageKeyOwner);
            this.owner = owner || "all"; // mine, unassigned
            this.$watch("owner", this.onOwnerChange, { deep: true });
        },
        initializePageSize() {
            var pageSize = this.$localStorageCache.get(
                this.localStorageKeyPageSize
            );
            this.pageSize = pageSize || 25;
        },
        async initAvailableColumns() {
            var cacheKey = "contacts_available_columns";
            var cached = this.$localStorageCache.get(cacheKey);
            if (cached) {
                this.availableColumns = JSON.parse(cached);
                return;
            }
            let accountColumns = new AvailableAccountColumns();
            let contactColumns = new AvailableContactColumns();
            await Promise.all([
                accountColumns.init(this.$i18n, this.$store),
                contactColumns.init(this.$i18n, this.$store),
            ]);
            contactColumns.children.push({
                children: accountColumns.children,
                definition: Object.assign({
                    label: this.$i18n.t("entityColumns.types.traitsAccounts"),
                }),
                label: this.$i18n.t("entityColumns.types.traitsAccounts"),
            });
            this.availableColumns = contactColumns;
            this.$localStorageCache.set(
                cacheKey,
                JSON.stringify(this.availableColumns),
                60 * 60 * 24
            );
        },
        async initAvailableFilters() {
            var cacheKey = "contacts_available_filters";
            var cached = this.$localStorageCache.get(cacheKey);
            if (cached) {
                this.availableFilters = JSON.parse(cached);
                return;
            }
            let accountFilters = new AvailableAccountFilters();
            let contactFilters = new AvailableContactFilters();
            await Promise.all([
                accountFilters.init(this.$i18n, this.$store),
                contactFilters.init(this.$i18n, this.$store),
            ]);
            contactFilters.children.push({
                children: accountFilters.children,
                label: this.$i18n.t("entityFilters.types.traitsAccounts"),
                definition: Object.assign({
                    label: this.$i18n.t("entityFilters.types.traitsAccounts"),
                }),
            });
            this.availableFilters = contactFilters;
            this.$localStorageCache.set(
                cacheKey,
                JSON.stringify(this.availableFilters),
                60 * 60 * 24
            );
        },
        async initAvailableStatistics() {
            var cacheKey = "contacts_available_statistics";
            var cached = this.$localStorageCache.get(cacheKey);
            if (cached) {
                this.availableStatistics = JSON.parse(cached);
                return;
            }
            this.availableStatistics = new AvailableStatistics();
            await this.availableStatistics.init(this.$i18n, this.$store);
            this.$localStorageCache.set(
                cacheKey,
                JSON.stringify(this.availableStatistics),
                60 * 60 * 24
            );
        },
        initializeStatistics: function () {
            this.selectedStatistics = [];

            var selectedStatistics = this.$localStorageCache.get(
                this.localStorageKeyStatistics
            );
            if (selectedStatistics) {
                this.selectedStatistics.push(...JSON.parse(selectedStatistics));
            }
            this.$watch("selectedStatistics", this.onSelectedStatisticsChange, {
                deep: true,
            });
        },
        async load() {
            this.initializeColumns();
            this.initializeSortBy();
            this.initializeOwner();
            this.initializePageSize();

            this.initAvailableColumns();

            await this.initAvailableFilters();
            this.initializeFilters();
            this.query();

            await this.initAvailableStatistics();
            this.initializeStatistics();
        },
        onSelectedStatisticsChange() {
            this.$localStorageCache.set(
                this.localStorageKeyStatistics,
                JSON.stringify(this.selectedStatistics)
            );
        },
        async query() {
            this.loading = true;
            let request = {
                q: JSON.stringify(this.filterGroup.compress()),
                columns: JSON.stringify(this.selectedColumns),
                modifiers: this.modifiers,
                sort_by: this.sortByFormatted,
                page: this.pageCurrent,
                per_page: this.pageSize,
                post_filters: JSON.stringify({
                    full_name: this.nameFilter,
                    email: this.emailFilter,
                }),
            };
            let response;
            try {
                response = await this.contactSearchRepository.index(request);
            } catch (e) {
                this.loading = false;
                this.data = [];
                this.pageCurrent = 1;
                // In case the error was caused by sorting
                _.merge(this.sortBy, {
                    column: null,
                    order: null,
                    dataTypeId: null,
                });
                return;
            }
            this.loading = false;
            this.data = _.get(response, "data");
            this.pageCurrent = _.get(response, "data.meta.current_page", 1);
            this.pageSize = _.get(response, "data.meta.per_page", 25);
        },
        onPageChange(page) {
            this.pageCurrent = page;
            this.query();
        },
        onPageSizeChange(size) {
            this.$localStorageCache.set(this.localStorageKeyPageSize, size);
            this.pageCurrent = 1;
            this.pageSize = size;
            this.query();
        },
        onSort(definition) {
            this.pageCurrent = 1;
            _.merge(this.sortBy, definition);
        },
        onEntityFiltersUpdated() {
            if (!this.filterGroup.valid()) {
                return false;
            }
            if (this.filterGroup.editing()) {
                return false;
            }
            this.$localStorageCache.set(
                this.localStorageKeyFilters,
                JSON.stringify(this.filterGroup)
            );
            this.query();
        },
        onColumnsChange() {
            this.$localStorageCache.set(
                this.localStorageKeyColumnsSelected,
                JSON.stringify(this.selectedColumns)
            );
            this.query();
        },
        onSortByChange() {
            this.$localStorageCache.set(
                this.localStorageKeySortBy,
                JSON.stringify(this.sortBy)
            );
            this.query();
        },
        onOwnerChange() {
            this.$localStorageCache.set(this.localStorageKeyOwner, this.owner);
            this.query();
        },
        async saveSegment(formData) {
            let response = await this.segmentsRepository.store({
                entity_type_id: 2,
                name: formData.name || "New Segment",
                type_id: 1,
                is_shared: formData.is_shared || false,
                tag_enabled: formData.tag_enabled || false,
                tag_name: formData.tag_name,
                tag_color: formData.tag_color,
                definition: JSON.stringify({
                    filters: this.filterGroup,
                    columns: this.selectedColumns,
                    sort_by: this.sortBy,
                    statistics: this.selectedStatistics,
                }),
            });
            var segment = response.data.data;
            response = await this.saveStatistics(segment.id);
            this.$router.push("/contacts/segments/" + segment.id);
            this.$root.$emit("segment:create", segment);
        },
    },
};
</script>
